import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, Trash2, Copy, Check, ArrowLeft } from 'lucide-react';
import { membershipApi, type Membership, type MembershipRole } from '@/lib/api';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useUpgradeModal } from '@/contexts/UpgradeModalContext';

export function TeamSettings() {
  const { businessId } = useParams<{ businessId: string }>();
  const { nav } = useBusinessNav();
  const { showUpgradeModal } = useUpgradeModal();
  const [members, setMembers] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<MembershipRole>('full_access');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Copy link state
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    loadMembers();
  }, [businessId]);

  const loadMembers = async () => {
    if (!businessId) return;
    setLoading(true);
    const response = await membershipApi.list(businessId);
    if (response.data) {
      setMembers(response.data);
    } else if (response.error) {
      setError(response.error);
    }
    setLoading(false);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) return;

    setInviteSubmitting(true);
    setInviteError('');

    const response = await membershipApi.create({
      email: inviteEmail,
      name: inviteName,
      role: inviteRole,
      business: businessId,
    });

    setInviteSubmitting(false);

    if (response.limitExceeded) {
      setInviteOpen(false);
      showUpgradeModal(response.limitExceeded);
      return;
    }

    if (response.error) {
      setInviteError(response.error);
      return;
    }

    // Refresh list and close modal
    loadMembers();
    setInviteOpen(false);
    setInviteEmail('');
    setInviteName('');
    setInviteRole('full_access');
  };

  const handleDelete = async (membership: Membership) => {
    if (!confirm(`Remove ${membership.display_name} from the team?`)) return;

    const response = await membershipApi.delete(membership.id);
    if (response.error) {
      alert(response.error);
      return;
    }

    loadMembers();
  };

  const copyInviteLink = (membershipId: string) => {
    const url = `${window.location.origin}/join/${membershipId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(membershipId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getRoleBadgeVariant = (role: MembershipRole): 'default' | 'secondary' | 'outline' => {
    switch (role) {
      case 'admin': return 'default';
      case 'full_access': return 'secondary';
      case 'view_only': return 'outline';
    }
  };

  const getRoleLabel = (role: MembershipRole) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'full_access': return 'Full Access';
      case 'view_only': return 'View Only';
    }
  };

  if (loading) return <div className="flex-1" />;

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav('/settings')}
          className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Button>
      </div>

      <div className="space-y-1 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Users className="h-6 w-6" />
          Team Members
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage who has access to this business.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-destructive/10 text-destructive rounded-lg">
          {error}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Team</CardTitle>
            <CardDescription>
              {members.length} member{members.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button onClick={() => setInviteOpen(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Invite
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {member.display_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{member.display_name}</p>
                      <Badge variant={getRoleBadgeVariant(member.role)}>
                        {getRoleLabel(member.role)}
                      </Badge>
                      {member.status === 'invited' && (
                        <Badge variant="outline" className="text-amber-600 border-amber-600">
                          Invited
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {member.display_email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {member.status === 'invited' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyInviteLink(member.id)}
                      className="gap-2"
                    >
                      {copiedId === member.id ? (
                        <>
                          <Check className="h-4 w-4 text-green-600" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy Link
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(member)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {members.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No team members yet. Invite someone to get started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your business.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                placeholder="John Doe"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="john@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as MembershipRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin - Full control</SelectItem>
                  <SelectItem value="full_access">Full Access - Can create and edit</SelectItem>
                  <SelectItem value="view_only">View Only - Read-only access</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {inviteError && (
              <p className="text-sm text-destructive">{inviteError}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviteSubmitting} className="gap-2">
                <UserPlus className="h-4 w-4" />
                {inviteSubmitting ? 'Sending...' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
